/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { CssBaseline } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Amplify, Auth } from 'aws-amplify';

import App from './App';
import { store } from './Shared/Store/store';
import reportWebVitals from './report-web-vitals';

import awsConfig from './Configuration/environment-config';
import i18n from './Bootstrap/i18n';
import { I18nextProvider } from 'react-i18next';
import { OTJCommHelperProvider } from 'otj-client-lib';
import { getChatUrls, getStoredCustomerDetails } from './Shared/Utilities/utils';
import { Environment } from './Shared/Constants/App';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

/**
 * Picks the redirect URL that matches the current browser origin from the
 * comma-separated list in the aws config.  Falls back to the first entry so
 * localhost development always works even when the list order changes.
 */
const resolveRedirectUrl = (urlList: string): string => {
  const urls = urlList.split(',').map((u) => u.trim());
  return urls.find((url) => url.startsWith(window.location.origin)) ?? urls[0];
};

Amplify.configure({
  ...awsConfig,
  oauth: {
    ...awsConfig.oauth,
    redirectSignIn: resolveRedirectUrl(awsConfig.oauth.redirectSignIn),
    redirectSignOut: resolveRedirectUrl(awsConfig.oauth.redirectSignOut),
  },
});

const [apiBaseUrl, mqttClusterBaseUrl] = getChatUrls(process.env.REACT_APP_ENVIRONMENT as Environment);

const { userId = '' } = getStoredCustomerDetails() || {};

const AppWithToken = () => {
  const [userToken, setUserToken] = useState<string>('');

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        setUserToken(idToken);
      } catch (e) {
        console.error('Failed to fetch Cognito token:', e);
      }
    };
    fetchToken();
  }, []);

  return (
    <OTJCommHelperProvider
      apiBaseUrl={apiBaseUrl}
      env={process.env.REACT_APP_ENVIRONMENT as undefined}
      mqttClusterBaseUrl={mqttClusterBaseUrl}
      userId={userId}
      userToken={userToken}
      refreshAccessToken={async () => {
        try {
          const session = await Auth.currentSession();
          return session.getIdToken().getJwtToken();
        } catch (e) {
          console.error('error occurred while refreshing AccessToken', e);
          return '';
        }
      }}
      isAccessTokenExpiring={async (chatToken: string | null) => {
        try {
          if (!chatToken) return false;
          const payload = JSON.parse(atob(chatToken.split('.')[1]));
          const expiryMs = payload.exp * 1000;
          const fiveMinutes = 5 * 60 * 1000;
          return Date.now() >= expiryMs - fiveMinutes;
        } catch (e) {
          console.error('error occurred while checking token expiring', e);
          return true;
        }
      }}>
      <Provider store={store}>
        <CssBaseline />
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Provider>
    </OTJCommHelperProvider>
  );
};

const root = createRoot(document.querySelector('#root') as HTMLElement);
root.render(<AppWithToken />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
